import {
  all,
  doesntHave,
  fallback,
  has,
  oneOfOrFallback,
  pageIs,
  pipe,
  transform,
  when
} from './utils'

import PageNames from 'pages/page-names'
import { keysToCamelCase } from 'common/utils/object'
import { lowerCaseString } from 'common/utils/string'

function normalizeHPPSettings(hppSettings): HPPSettings {
  return pipe(
    keysToCamelCase,
    when(
      pageIs(PageNames.IN_STORE_TOKENS_BOOTSTRAP),
      transform({
        page: () => PageNames.IST_BOOTSTRAP
      })
    ),
    when(
      pageIs(PageNames.PAYMENT_BOOTSTRAP_LIGHT),
      transform({
        page: () => PageNames.PAYMENT_BOOTSTRAP_EXPRESS
      })
    ),
    transform({
      issuer: fallback('klarna'),
      page: oneOfOrFallback(Object.values(PageNames), PageNames.GENERIC_ERROR),
      actions: {
        error: fallback('https://x.klarnacdn.net/hpp/v2/error.html')
      }
    }),
    when(
      has('payment'),
      transform({
        payment: {
          purchaseType: oneOfOrFallback(
            ['BOOK', 'BUY', 'CONTINUE', 'DOWNLOAD', 'ORDER', 'RENT', 'SUBSCRIBE', 'PAY'],
            'BUY'
          )
        }
      })
    ),
    when(
      has('kp'),
      transform({
        kp: {
          fallback: {
            loadRejectionMessaging: oneOfOrFallback(
              ['CHANGE_KLARNA_PAYMENT_METHOD', 'CHANGE_PAYMENT_METHOD', 'CORRECT_BILLING_ADDRESS'],
              null
            ),
            errorOnLoadRejectionMessaging: oneOfOrFallback(
              ['MODIFY_BILLING_ADDRESS_ON_MERCHANT', 'CORRECT_BILLING_ADDRESS'],
              null
            ),
            authorizeRejectionMessaging: oneOfOrFallback(
              ['CHANGE_KLARNA_PAYMENT_METHOD', 'CHANGE_PAYMENT_METHOD'],
              null
            ),
            errorOnAuthorizeRejectionMessaging: oneOfOrFallback(
              ['CHANGE_KLARNA_PAYMENT_METHOD', 'CHANGE_PAYMENT_METHOD'],
              null
            )
          }
        }
      })
    ),
    when(
      has('order.orderLines'),
      transform({
        order: {
          orderLines: originalOrderLines => {
            const specialEntriesMap = {
              gift_card: { amount: 0, entries: 0 },
              discount: { amount: 0, entries: 0 },
              shipping_fee: { amount: 0, entries: 0 },
              surcharge: { amount: 0, entries: 0 },
              store_credit: { amount: 0, entries: 0 }
            }

            const normalizedOrderLines = []

            for (let orderLine of originalOrderLines) {
              if (specialEntriesMap.hasOwnProperty(orderLine.type)) {
                specialEntriesMap[orderLine.type].amount += orderLine.totalAmount
                specialEntriesMap[orderLine.type].entries += 1
              } else {
                normalizedOrderLines.push(orderLine)
              }
            }

            for (let key in specialEntriesMap) {
              const totalAmount = specialEntriesMap[key].amount

              if (totalAmount) {
                normalizedOrderLines.push({
                  type: 'special_entry',
                  entries: specialEntriesMap[key].entries,
                  name: key,
                  totalAmount
                })
              }
            }

            return normalizedOrderLines
          }
        }
      })
    ),
    when(
      all(pageIs(PageNames.ELIGIBILITY_SUCCESS), doesntHave('eligibility.success.footer.type')),
      transform({
        eligibility: {
          success: {
            footer: fallback({
              type: 'MESSAGE',
              message: {}
            })
          }
        }
      })
    ),
    when(
      has('instantShopping'),
      transform({
        instantShopping: {
          environment: pipe(
            lowerCaseString,
            oneOfOrFallback(['staging', 'playground', 'production'], 'staging')
          ),
          region: pipe(lowerCaseString, oneOfOrFallback(['eu', 'na'], 'eu'))
        }
      })
    ),
    when(
      pageIs(PageNames.SHOPPING_WALLET_BOOTSTRAP_STEPS),
      transform({
        actions: {
          landingPage: fallback('https://shop.klarna.com/coach')
        }
      })
    ),
    // Due to bubble-ui overrides only accepting snake case properties we added this
    // to make every theme property to remain in snake case
    when(
      has('theme'),
      transform({
        theme: () => hppSettings.theme
      })
    )
  )(hppSettings)
}

export default normalizeHPPSettings
